import React from 'react'
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import { CreditCard, Pix, Security, Speed } from '@mui/icons-material'
import type { PaymentMethod } from '../../types/cart'

interface PaymentMethodSelectorProps {
  selectedMethod: PaymentMethod
  onMethodChange: (method: PaymentMethod) => void
  totalAmount: number
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({ selectedMethod, onMethodChange, totalAmount }) => {
  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price)

  const isCardEnabled = totalAmount >= 3.0

  React.useEffect(() => {
    if (!isCardEnabled && selectedMethod === 'CARTAO_CREDITO') onMethodChange('PIX_DIRETO')
  }, [isCardEnabled, selectedMethod, onMethodChange])

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={900} sx={{ mb: 2 }}>
        Forma de pagamento
      </Typography>

      <FormControl component="fieldset" fullWidth>
        <RadioGroup value={selectedMethod} onChange={(e) => onMethodChange(e.target.value as PaymentMethod)}>
          <Stack spacing={2}>
            <Card
              onClick={() => onMethodChange('PIX_DIRETO')}
              sx={{
                cursor: 'pointer',
                border: selectedMethod === 'PIX_DIRETO' ? '2px solid #00e676' : '1px solid rgba(255,255,255,0.10)',
                backgroundColor: selectedMethod === 'PIX_DIRETO' ? 'rgba(0,230,118,0.06)' : 'transparent',
                '&:hover': { borderColor: '#00e676' }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel value="PIX_DIRETO" control={<Radio />} label="" sx={{ m: 0 }} />
                    <Avatar sx={{ bgcolor: '#32BCAD', width: 46, height: 46 }}>
                      <Pix />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={900}>
                        PIX
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                        <Chip icon={<Speed />} label="Instantâneo" size="small" sx={{ bgcolor: 'rgba(50,188,173,0.18)', color: '#32BCAD' }} />
                        <Chip icon={<Security />} label="Seguro" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.18)', color: '#4caf50' }} />
                      </Stack>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" fontWeight={900} color="primary">
                      {formatPrice(totalAmount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sem taxas
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card
              onClick={isCardEnabled ? () => onMethodChange('CARTAO_CREDITO') : undefined}
              sx={{
                cursor: isCardEnabled ? 'pointer' : 'not-allowed',
                opacity: isCardEnabled ? 1 : 0.55,
                border:
                  selectedMethod === 'CARTAO_CREDITO' ? '2px solid #00e676' : '1px solid rgba(255,255,255,0.10)',
                backgroundColor: selectedMethod === 'CARTAO_CREDITO' ? 'rgba(0,230,118,0.06)' : 'transparent',
                '&:hover': isCardEnabled ? { borderColor: '#00e676' } : {}
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      value="CARTAO_CREDITO"
                      control={<Radio disabled={!isCardEnabled} />}
                      label=""
                      sx={{ m: 0 }}
                    />
                    <Avatar sx={{ bgcolor: isCardEnabled ? '#1976d2' : '#666', width: 46, height: 46 }}>
                      <CreditCard />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={900} sx={{ color: isCardEnabled ? 'inherit' : 'text.disabled' }}>
                        Cartão de crédito
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visa, Mastercard, Elo…
                      </Typography>
                      {!isCardEnabled && (
                        <Typography variant="caption" color="text.secondary">
                          Disponível a partir de {formatPrice(3)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" fontWeight={900}>
                      {formatPrice(totalAmount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Parcelamento
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </RadioGroup>
      </FormControl>
    </Box>
  )
}

export default PaymentMethodSelector

